import { BrandLockup } from '@/components/brand/BrandMark';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCompany } from '@/hooks/use-company';
import { usePermissions } from '@/hooks/use-permissions';
import { getApiErrorMessage } from '@/lib/api/error';
import { cn } from '@/lib/utils';
import { Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { CompanySwitcherPanel } from './CompanySwitcherPanel';
import {
  getNavigationSections,
  isNavigationItemActive,
  isNavigationSectionActive,
  type NavigationSection,
} from './navigation-config';
export function AppSidebar() {
  const { activeCompany, userCompanies, setActiveCompany } = useCompany();
  const { canAccess } = usePermissions();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const currentLocation = `${pathname}${search}`;
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);

  const sections = getNavigationSections({ activeCompany, canAccess });

  const getSectionStateKey = (sectionId: string) => `${pathname}:${sectionId}`;

  const toggleSection = (section: NavigationSection, nextOpen: boolean) => {
    setOpenSections((current) => ({
      ...current,
      [getSectionStateKey(section.id)]: nextOpen,
    }));
  };

  const handleSelectCompany = (company: (typeof userCompanies)[number]['company']) => {
    void setActiveCompany(company)
      .then(() => {
        setCompanySwitcherOpen(false);
      })
      .catch((error) => {
        toast.error('Erro ao trocar empresa', {
          description: getApiErrorMessage(error, 'Não foi possível gerar a sessão da empresa selecionada.'),
        });
      });
  };

  return (
    <aside className="bg-sidebar border-sidebar-border fixed top-0 left-0 z-40 hidden h-dvh w-80 flex-col overflow-hidden border-r lg:flex">
      <div className="border-sidebar-border flex h-20 items-center gap-3 border-b px-6">
        <BrandLockup textClassName="text-sidebar-foreground text-xl" />
      </div>

      <div className="border-sidebar-border border-b px-5 py-5">
        <Popover open={companySwitcherOpen} onOpenChange={setCompanySwitcherOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className="bg-sidebar-muted/65 hover:bg-sidebar-muted text-sidebar-foreground hover:text-sidebar-foreground border-sidebar-border/70 h-auto w-full justify-between rounded-2xl border px-4 py-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary/20 flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl">
                  {activeCompany?.profilePhoto ? (
                    <img
                      src={activeCompany.profilePhoto}
                      alt={activeCompany.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Building2 className="text-primary h-5 w-5" />
                  )}
                </div>
                <div className="text-left">
                  <p className="max-w-44 truncate text-sm font-semibold">{activeCompany?.name || 'Selecionar'}</p>
                  <p className="text-sidebar-foreground/70 mt-0.5 text-xs">Empresa ativa</p>
                </div>
              </div>
              <ChevronDown className="text-sidebar-foreground/70 h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-104 rounded-3xl p-4">
            <CompanySwitcherPanel
              companies={userCompanies}
              activeCompanyId={activeCompany?.id}
              onSelectCompany={handleSelectCompany}
              onCreateCompany={() => {
                setCompanySwitcherOpen(false);
                navigate('/onboarding?mode=create-company');
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <nav className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-6">
        <div className="space-y-2">
          {sections.map((section) => {
            const isActive = isNavigationSectionActive(currentLocation, section);

            if (section.path) {
              return (
                <NavLink
                  key={section.id}
                  to={section.path}
                  className={cn(
                    'flex items-center gap-3 rounded-2xl px-2.5 py-2.5 text-sm font-semibold transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-sidebar-foreground hover:bg-sidebar-muted/60',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                      isActive ? 'bg-primary-foreground/15' : 'text-sidebar-foreground/78',
                    )}
                  >
                    <section.icon className="h-4.5 w-4.5" />
                  </span>
                  <span className="truncate">{section.label}</span>
                </NavLink>
              );
            }

            const sectionStateKey = getSectionStateKey(section.id);
            const isOpen = openSections[sectionStateKey] ?? isActive;
            return (
              <Collapsible key={section.id} open={isOpen} onOpenChange={(nextOpen) => toggleSection(section, nextOpen)}>
                <div
                  className={cn(
                    'rounded-2xl',
                    isActive && 'bg-sidebar-muted/60 ring-sidebar-border/70 shadow-sm ring-1',
                  )}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'hover:bg-sidebar-muted/60 flex w-full items-center justify-between rounded-2xl px-2.5 py-2.5 text-left transition-colors',
                        isActive && 'bg-sidebar-muted/85',
                      )}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={cn(
                            'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                            isActive ? 'bg-primary text-primary-foreground' : 'text-sidebar-foreground/78',
                          )}
                        >
                          <section.icon className="h-4.5 w-4.5" />
                        </div>
                        <p className="text-sidebar-foreground truncate text-sm font-semibold">{section.label}</p>
                      </div>
                      <ChevronRight
                        className={cn(
                          'text-sidebar-foreground/75 h-4 w-4 shrink-0 transition-transform',
                          isOpen && 'rotate-90',
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="border-sidebar-border/70 mr-1 ml-5 space-y-1.5 border-l pt-1 pb-1 pl-4">
                    {section.items.map((item) => (
                      <NavLink
                        key={item.id}
                        to={item.path}
                        className={() =>
                          cn(
                            'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all',
                            isNavigationItemActive(currentLocation, item)
                              ? 'bg-primary text-primary-foreground shadow-glow'
                              : 'text-sidebar-foreground/90 hover:bg-sidebar-muted/70 hover:text-sidebar-foreground',
                          )
                        }
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 truncate font-medium">{item.label}</span>
                      </NavLink>
                    ))}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      </nav>
    </aside>
  );
}
